import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { adminAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole, isStaffRole } from '../../utils/roles';

const GateStaffPage = () => {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [event, setEvent] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    employerType: 'ORGANIZER'
  });

  const isAdmin = isAdminRole(user?.role);

  const notify = (options) =>
    Swal.fire({
      confirmButtonColor: '#ea580c',
      ...options
    });

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const [eventRes, staffRes] = await Promise.all([
        adminAPI.getEvent(eventId),
        adminAPI.getGateStaff(eventId)
      ]);
      setEvent(eventRes.data?.event || eventRes.data || null);
      setStaff(staffRes.data?.staff || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load gate staff');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (isStaffRole(user?.role)) load();
  }, [user, load]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      notify({ icon: 'warning', title: 'Name and email are required' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || undefined,
        password: form.password.trim() || undefined
      };
      if (isAdmin) payload.employerType = form.employerType;

      const res = await adminAPI.createGateStaff(eventId, payload);
      const temp = res.data?.temporaryPassword;
      await load();
      setForm({
        name: '',
        email: '',
        phone: '',
        password: '',
        employerType: isAdmin ? 'PLATFORM' : 'ORGANIZER'
      });

      if (temp) {
        await notify({
          icon: 'success',
          title: 'Gate staff created',
          html: `<p class="text-left mb-2">Share these login details for the <strong>Niko Vibe Gate</strong> app:</p>
            <p class="text-left text-sm"><strong>Email:</strong> ${payload.email}</p>
            <p class="text-left text-sm"><strong>Temp password:</strong> <code>${temp}</code></p>
            <p class="text-left text-xs text-gray-500 mt-2">They will only see this event in the gate app.</p>`
        });
      } else {
        await notify({
          icon: 'success',
          title: 'Assigned',
          text: res.message || 'Gate staff assigned to this event.'
        });
      }
    } catch (err) {
      notify({
        icon: 'error',
        title: 'Could not add staff',
        text: err.response?.data?.message || err.message
      });
    } finally {
      setSaving(false);
    }
  };

  const onRemove = async (row) => {
    const result = await Swal.fire({
      title: 'Remove from this event?',
      text: `${row.user?.name || 'Staff'} will no longer see this event in the gate app.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ea580c',
      confirmButtonText: 'Remove'
    });
    if (!result.isConfirmed) return;
    try {
      await adminAPI.removeGateStaff(eventId, row.id);
      await load();
      notify({ icon: 'success', title: 'Removed', timer: 1800, showConfirmButton: false });
    } catch (err) {
      notify({
        icon: 'error',
        title: 'Failed',
        text: err.response?.data?.message || err.message
      });
    }
  };

  const onResetPassword = async (row) => {
    if (row.user?.role !== 'GATE_STAFF') {
      notify({
        icon: 'info',
        title: 'Not available',
        text: 'Password reset here is only for gate staff accounts (not organizers).'
      });
      return;
    }
    const result = await Swal.fire({
      title: 'Reset password?',
      text: `Generate a new temporary password for ${row.user?.name}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#ea580c',
      confirmButtonText: 'Reset'
    });
    if (!result.isConfirmed) return;
    try {
      const res = await adminAPI.resetGateStaffPassword(eventId, row.id);
      const temp = res.data?.temporaryPassword;
      await notify({
        icon: 'success',
        title: 'Password reset',
        html: `<p class="text-left text-sm"><strong>Email:</strong> ${row.user?.email}</p>
          <p class="text-left text-sm"><strong>Temp password:</strong> <code>${temp}</code></p>`
      });
    } catch (err) {
      notify({
        icon: 'error',
        title: 'Failed',
        text: err.response?.data?.message || err.message
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="lg:pl-72">
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} title="Gate staff" />
        <main className="p-4 sm:p-6 lg:p-8 max-w-5xl">
          <div className="mb-6">
            <Link to="/admin/events" className="text-sm text-orange-600 hover:text-orange-700 font-medium">
              ← Back to events
            </Link>
            <h1 className="mt-2 text-2xl font-bold text-gray-900">
              {event?.title || 'Gate staff'}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Create door accounts for the Niko Vibe Gate app. They only see events you assign them to.
            </p>
          </div>

          {error ? (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          <div className="grid gap-6 lg:grid-cols-5">
            <form
              onSubmit={onCreate}
              className="lg:col-span-2 rounded-xl border border-orange-100 bg-white p-5 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-gray-900">Add gate staff</h2>
              <p className="mt-1 text-xs text-gray-500 mb-4">
                Leave password blank to auto-generate a temporary one.
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-1">Full name</label>
              <input
                className="w-full mb-3 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Jane Door"
                required
              />

              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                className="w-full mb-3 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="jane@example.com"
                required
              />

              <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
              <input
                className="w-full mb-3 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="2547…"
              />

              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password (optional)
              </label>
              <input
                type="text"
                className="w-full mb-3 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder="Auto-generate if empty"
                minLength={6}
              />

              {isAdmin ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Crew type</label>
                  <select
                    className="w-full mb-4 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    value={form.employerType}
                    onChange={(e) => setForm((f) => ({ ...f, employerType: e.target.value }))}
                  >
                    <option value="PLATFORM">Niko Vibe platform crew</option>
                    <option value="ORGANIZER">Organizer crew</option>
                  </select>
                </>
              ) : null}

              <button
                type="submit"
                disabled={saving || loading}
                className="w-full rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
              >
                {saving ? 'Saving…' : 'Create & assign'}
              </button>
            </form>

            <div className="lg:col-span-3 rounded-xl border border-gray-100 bg-white shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 px-5 py-4">
                <h2 className="text-lg font-semibold text-gray-900">Assigned to this event</h2>
              </div>
              {loading ? (
                <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
              ) : staff.length === 0 ? (
                <div className="p-8 text-center text-sm text-gray-500">
                  No door staff yet. Add someone on the left.
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {staff.map((row) => (
                    <li key={row.id} className="px-5 py-4 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-gray-900">{row.user?.name}</p>
                          {!row.isActive ? (
                            <span className="text-xs rounded-full bg-gray-100 text-gray-600 px-2 py-0.5">
                              Inactive
                            </span>
                          ) : (
                            <span className="text-xs rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5">
                              Active
                            </span>
                          )}
                          <span className="text-xs rounded-full bg-orange-50 text-orange-700 px-2 py-0.5">
                            {row.employerType === 'PLATFORM' ? 'Niko Vibe crew' : 'Organizer crew'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 truncate">{row.user?.email}</p>
                        {row.user?.phone ? (
                          <p className="text-xs text-gray-500">{row.user.phone}</p>
                        ) : null}
                      </div>
                      {row.isActive ? (
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => onResetPassword(row)}
                            className="text-xs font-medium text-orange-700 hover:underline text-right"
                          >
                            Reset password
                          </button>
                          <button
                            type="button"
                            onClick={() => onRemove(row)}
                            className="text-xs font-medium text-red-600 hover:underline text-right"
                          >
                            Remove
                          </button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default GateStaffPage;
